import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Calendar, Gauge, Fuel, ChevronRight, Filter, SlidersHorizontal, Settings } from 'lucide-react';
import VehicleModal from './VehicleModal';
import VehicleImageCarousel from './VehicleImageCarousel';

const UsedVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres de base
  const [activeBrand, setActiveBrand] = useState('Toutes');
  const [maxPrice, setMaxPrice] = useState('Tous');
  const [sortBy, setSortBy] = useState('recent'); // recent, priceAsc, priceDesc, mileage

  // Filtres avancés
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeModel, setActiveModel] = useState('Tous');
  const [fuelType, setFuelType] = useState('Tous');
  const [transmission, setTransmission] = useState('Toutes');
  const [maxMileage, setMaxMileage] = useState('Tous');
  const [minYear, setMinYear] = useState('Toutes');

  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Remise à zéro du modèle quand on change de marque
  useEffect(() => {
    setActiveModel('Tous');
  }, [activeBrand]);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      // Récupérer uniquement les véhicules disponibles
      const records = await pb.collection('car_listings').getFullList({
        filter: 'status = "available"',
        sort: '-created',
      });
      setVehicles(records);
    } catch (err) {
      console.error('Erreur de récupération des annonces:', err);
      setError('Impossible de charger le catalogue des véhicules.');
    } finally {
      setLoading(false);
    }
  };

  // Liste dynamique des marques disponibles en stock
  const availableBrands = ['Toutes', ...new Set(vehicles.map(v => v.make))].filter(Boolean);
  
  // Modèles disponibles uniquement pour la marque sélectionnée
  const availableModels = ['Tous', ...new Set(
    vehicles.filter(v => activeBrand === 'Toutes' || v.make === activeBrand).map(v => v.model)
  )].filter(Boolean);

  // Application des filtres et tri
  const filteredVehicles = vehicles
    .filter(v => activeBrand === 'Toutes' || v.make === activeBrand)
    .filter(v => activeModel === 'Tous' || v.model === activeModel)
    .filter(v => fuelType === 'Tous' || v.fuel_type === fuelType)
    .filter(v => transmission === 'Toutes' || v.transmission === transmission)
    .filter(v => {
      if (maxPrice === 'Tous') return true;
      const price = parseFloat(v.price);
      if (maxPrice === '5000') return price <= 5000;
      if (maxPrice === '10000') return price <= 10000;
      if (maxPrice === '15000') return price <= 15000;
      if (maxPrice === '20000') return price <= 20000;
      if (maxPrice === '30000') return price <= 30000;
      return true;
    })
    .filter(v => {
      if (maxMileage === 'Tous') return true;
      const mileage = parseFloat(v.mileage);
      if (maxMileage === '30000') return mileage <= 30000;
      if (maxMileage === '70000') return mileage <= 70000;
      if (maxMileage === '120000') return mileage <= 120000;
      if (maxMileage === '150000') return mileage <= 150000;
      return true;
    })
    .filter(v => {
      if (minYear === 'Toutes') return true;
      const year = parseInt(v.year, 10);
      return year >= parseInt(minYear, 10);
    })
    .sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.created) - new Date(a.created);
      if (sortBy === 'priceAsc') return parseFloat(a.price) - parseFloat(b.price);
      if (sortBy === 'priceDesc') return parseFloat(b.price) - parseFloat(a.price);
      if (sortBy === 'mileage') return parseFloat(a.mileage) - parseFloat(b.mileage);
      return 0;
    });

  return (
    <section id="used-vehicles" className="catalog-section">
      <div className="container">
        {/* Header de la section */}
        <div className="catalog-header">
          <span className="badge">Catalogue Occasions</span>
          <h2 className="title">Véhicules d'Occasion Expertisés</h2>
          <p className="subtitle">
            Chaque véhicule de notre parc est rigoureusement inspecté par <strong>Christophe Dépanne</strong> 
            pour vous garantir une fiabilité totale et une sérénité absolue.
          </p>
        </div>

        {/* Barre de Filtres Principale */}
        <div className="filters-container shadow-sm">
          <div className="filters-basic">
            <div className="filter-group">
              <label><Filter size={16} /> Marque</label>
              <select value={activeBrand} onChange={(e) => setActiveBrand(e.target.value)}>
                {availableBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Prix max</label>
              <select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}>
                <option value="Tous">Peu importe</option>
                <option value="5000">Jusqu'à 5 000 €</option>
                <option value="10000">Jusqu'à 10 000 €</option>
                <option value="15000">Jusqu'à 15 000 €</option>
                <option value="20000">Jusqu'à 20 000 €</option>
                <option value="30000">Jusqu'à 30 000 €</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Trier par</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="recent">Les plus récents</option>
                <option value="priceAsc">Prix croissant</option>
                <option value="priceDesc">Prix décroissant</option>
                <option value="mileage">Faible kilométrage</option>
              </select>
            </div>
          </div>

          {/* Toggle Filtres Avancés */}
          <button 
            className={`adv-filters-btn ${showAdvancedFilters ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <SlidersHorizontal size={18} />
            {showAdvancedFilters ? 'Masquer filtres avancés' : 'Plus de filtres'}
          </button>

          {/* Filtres Avancés (Accordéon) */}
          {showAdvancedFilters && (
            <div className="filters-advanced slide-down">
              
              <div className="filter-group">
                <label>Modèle</label>
                <select 
                  value={activeModel} 
                  onChange={(e) => setActiveModel(e.target.value)}
                  disabled={activeBrand === 'Toutes' && availableModels.length <= 1}
                >
                  {availableModels.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Carburant</label>
                <select value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
                  <option value="Tous">Tous</option>
                  <option value="Essence">Essence</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Electrique">Électrique</option>
                  <option value="Hybride">Hybride</option>
                  <option value="GPL">GPL</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Boîte</label>
                <select value={transmission} onChange={(e) => setTransmission(e.target.value)}>
                  <option value="Toutes">Toutes</option>
                  <option value="manual">Manuelle</option>
                  <option value="automatic">Automatique</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Kilométrage max</label>
                <select value={maxMileage} onChange={(e) => setMaxMileage(e.target.value)}>
                  <option value="Tous">Peu importe</option>
                  <option value="30000">- de 30 000 km</option>
                  <option value="70000">- de 70 000 km</option>
                  <option value="120000">- de 120 000 km</option>
                  <option value="150000">- de 150 000 km</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Année min.</label>
                <select value={minYear} onChange={(e) => setMinYear(e.target.value)}>
                  <option value="Toutes">Peu importe</option>
                  <option value="2022">À partir de 2022</option>
                  <option value="2018">À partir de 2018</option>
                  <option value="2015">À partir de 2015</option>
                  <option value="2010">À partir de 2010</option>
                </select>
              </div>

            </div>
          )}
        </div>

        {/* Grille des véhicules */}
        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Chargement du catalogue...</p>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredVehicles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🚗</div>
            <h3>Aucun véhicule ne correspond à vos critères.</h3>
            <button className="reset-btn" onClick={() => {
              setActiveBrand('Toutes'); setMaxPrice('Tous');
              setActiveModel('Tous'); setFuelType('Tous'); setTransmission('Toutes');
              setMaxMileage('Tous'); setMinYear('Toutes');
            }}>
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <div className="vehicles-grid">
            {filteredVehicles.map((car) => (
              <div key={car.id} className="car-card" onClick={() => setSelectedVehicle(car)}>
                <div className="car-image-box">
                  <VehicleImageCarousel 
                    images={[...(car.cover_image ? [car.cover_image] : []), ...(car.images || [])]} 
                    carId={car.id} 
                    altText={`${car.make} ${car.model}`}
                    thumbSize="400x300"
                  />
                  <div className="price-overlay">{car.price} €</div>
                </div>
                
                <div className="car-info">
                  <h3 className="car-title">{car.title}</h3>
                  <span className="car-subtitle">{car.make} {car.model}</span>
                  
                  <div className="car-specs">
                    <div className="spec-item">
                      <Calendar size={16} />
                      <span>{car.year}</span>
                    </div>
                    <div className="spec-item">
                      <Gauge size={16} />
                      <span>{car.mileage} km</span>
                    </div>
                    <div className="spec-item">
                      <Fuel size={16} />
                      <span>{car.fuel_type || '-'}</span>
                    </div>
                    <div className="spec-item">
                      <Settings size={16} />
                      <span>{car.transmission === 'automatic' || car.transmission === 'Automatique' ? 'Automatique' : 'Manuelle'}</span>
                    </div>
                  </div>

                  <button className="view-btn">
                    Voir l'offre
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Section Reprise / Achat */}
        <div className="buyback-container">
          <div className="buyback-cta">
            <div className="cta-content">
              <h3>Vous souhaitez vendre votre voiture ?</h3>
              <p>Nous rachetons vos véhicules au meilleur prix avec paiement immédiat et sécurisé.</p>
            </div>
            <a href="/contact" className="cta-action-btn shadow-lg">Demander une estimation</a>
          </div>
        </div>
      </div>

      {selectedVehicle && (
        <VehicleModal vehicle={selectedVehicle} onClose={() => setSelectedVehicle(null)} />
      )}

      <style jsx>{`
        .catalog-section {
          padding: 80px 0;
          background-color: #f8fafc;
          min-height: 100vh;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .catalog-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 40px;
        }

        .badge {
          display: inline-block;
          background: #3b82f6;
          color: white;
          padding: 6px 18px;
          border-radius: 50px;
          font-weight: 800;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 25px;
        }

        .title {
          font-size: 3rem;
          color: #1e293b;
          font-weight: 900;
          margin-bottom: 20px;
          line-height: 1.1;
        }

        .subtitle {
          color: #64748b;
          font-size: 1.15rem;
          line-height: 1.6;
        }

        .filters-container {
          background: white;
          border-radius: 16px;
          padding: 25px 30px;
          margin-bottom: 40px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          border: 1px solid #f1f5f9;
        }

        .filters-basic {
          display: flex;
          gap: 30px;
        }

        .adv-filters-btn {
          margin-top: 20px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #3b82f6;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          padding: 0;
          transition: 0.3s;
        }

        .adv-filters-btn:hover {
          color: #2563eb;
        }

        .filters-advanced {
          margin-top: 25px;
          padding-top: 25px;
          border-top: 1px dashed #e2e8f0;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }

        .slide-down {
          animation: slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .filter-group {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group label {
          font-size: 0.85rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .filter-group select {
          padding: 12px 16px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          font-weight: 600;
          color: #1e293b;
          font-size: 1rem;
          cursor: pointer;
          outline: none;
          transition: all 0.2s;
        }

        .filter-group select:focus {
          border-color: #3b82f6;
          background: white;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* Grille de voitures */
        .vehicles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 30px;
        }

        .car-card {
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid rgba(0,0,0,0.02);
          cursor: pointer;
          display: flex;
          flex-direction: column;
        }

        .car-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }

        .car-image-box {
          position: relative;
          height: 220px;
          overflow: hidden;
          background: #f1f5f9;
        }

        .no-img-placeholder { font-size: 4rem; }

        .price-overlay {
          position: absolute;
          bottom: 15px;
          right: 15px;
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(10px);
          color: white;
          padding: 8px 16px;
          border-radius: 12px;
          font-weight: 900;
          font-size: 1.1rem;
          border: 1px solid rgba(255,255,255,0.1);
        }

        .car-info {
          padding: 25px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .car-title {
          font-size: 1.3rem;
          color: #1e293b;
          font-weight: 900;
          margin-bottom: 5px;
          line-height: 1.2;
        }

        .car-subtitle {
          color: #64748b;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 20px;
        }

        .car-specs {
          display: flex;
          justify-content: space-between;
          border-top: 1px solid #f1f5f9;
          padding-top: 15px;
          margin-bottom: 25px;
        }

        .spec-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 0.85rem;
        }
        .spec-item span { font-weight: 700; color: #1e293b; }

        .view-btn {
          margin-top: auto;
          width: 100%;
          padding: 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          color: #1e293b;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: 0.3s;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-size: 0.85rem;
        }

        .car-card:hover .view-btn {
          background: #1e293b;
          color: white;
          border-color: #1e293b;
        }

        /* Empty State */
        .empty-state { text-align: center; padding: 60px 0; color: #64748b; }
        .empty-icon { font-size: 4rem; margin-bottom: 15px; }
        .empty-state h3 { font-size: 1.5rem; color: #1e293b; margin-bottom: 20px; }
        .reset-btn {
          background: #3b82f6; color: white; padding: 12px 24px; border-radius: 8px; border: none; font-weight: 700; cursor: pointer;
        }

        .loading-state { text-align: center; padding: 80px 0; color: #64748b; }
        .loader { width: 40px; height: 40px; border: 4px solid #f1f5f9; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Reprise CTA Container */
        .buyback-container {
          margin-top: 80px;
        }

        .buyback-cta {
          background: #f0f9ff;
          border-radius: 32px;
          padding: 60px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #1e293b;
          gap: 40px;
          border: 1px solid #e0f2fe;
          box-shadow: 0 20px 50px rgba(0,0,0,0.03);
        }

        .cta-content h3 { font-size: 2.5rem; font-weight: 900; margin-bottom: 15px; color: #1e293b; }
        .cta-content p { color: #64748b; font-size: 1.2rem; font-weight: 600; }

        .cta-action-btn {
          background: #3b82f6;
          color: white;
          padding: 20px 40px;
          border-radius: 14px;
          text-decoration: none;
          font-weight: 800;
          white-space: nowrap;
          transition: all 0.3s;
          text-transform: uppercase;
          letter-spacing: 2px;
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
        }

        .cta-action-btn:hover {
          background: #2563eb;
          transform: scale(1.05) translateY(-3px);
          box-shadow: 0 15px 30px rgba(37, 99, 235, 0.4);
        }

        @media (max-width: 900px) {
          .filters-basic { flex-direction: column; gap: 15px; }
          .buyback-cta { flex-direction: column; text-align: center; padding: 40px 30px; }
          .title { font-size: 2.2rem; }
        }
      `}</style>
    </section>
  );
};

export default UsedVehicles;